import { useEffect, useState } from "react";
import { ArrowDownToLine, Calculator, Download, LoaderCircle } from "lucide-react";
import { api, downloadFile } from "./api";
import { MetricStrip, Pagination } from "./components";
import type { ImportJob } from "./types";
import type { OfficialPricingBatch } from "../server/official-pricing";

const storageKey = "meliforge-official-pricing-job";
const number = (value: number | null | undefined) => value == null ? "—" : value.toLocaleString("zh-CN", { maximumFractionDigits: 2 });

export function OfficialPricingPanel({ disabled, onUseFile }: { disabled: boolean; onUseFile(file: File): void }) {
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState(() => localStorage.getItem(storageKey));
  const [job, setJob] = useState<ImportJob<OfficialPricingBatch> | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [page, setPage] = useState(1);
  const pending = Boolean(jobId) && (!job || ["QUEUED", "PROCESSING"].includes(job.status));
  const result = job?.status === "COMPLETED" ? job.result : null;
  useEffect(() => {
    if (!jobId) return;
    let stopped = false;
    let timer: number | undefined;
    async function poll() {
      let repeat = true;
      try {
        const next = await api.importJob<OfficialPricingBatch>(jobId!);
        if (stopped) return;
        if (next.kind !== "OFFICIAL_PRICING_QUOTE") throw new Error("询价批次类型不匹配。");
        setJob(next); setError("");
        if (["COMPLETED", "FAILED"].includes(next.status)) {
          repeat = false;
          if (next.status === "FAILED") setError(next.errorMessage || "官方询价失败。");
        }
      } catch (cause) { if (!stopped) setError(cause instanceof Error ? cause.message : "读取询价进度失败。"); }
      // Schedule after completion so a slow server never stacks polling requests.
      if (!stopped && repeat) timer = window.setTimeout(() => void poll(), 2000);
    }
    void poll();
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [jobId]);

  async function start() {
    if (!file) return;
    setBusy("query"); setError(""); setNotice("");
    try {
      const body = new FormData(); body.append("file", file);
      const response = await fetch("/api/official-pricing/quotes", { method: "POST", body });
      const payload = await response.json() as ImportJob<OfficialPricingBatch> & { error?: string };
      if (!response.ok) throw new Error(payload.error || "官方询价无法启动。");
      setJob(payload); setJobId(payload.id); setPage(1); localStorage.setItem(storageKey, payload.id);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "询价失败。"); }
    finally { setBusy(""); }
  }
  async function action(kind: "template" | "export" | "use") {
    setBusy(kind); setError(""); setNotice("");
    try {
      if (kind === "template") await downloadFile("/api/official-pricing/template", "官方运费与调价模板.xlsx");
      else if (job) {
        const url = `/api/official-pricing/quotes/${encodeURIComponent(job.id)}/export`;
        if (kind === "export") await downloadFile(url, "官方运费与调价结果.xlsx");
        else {
          const response = await fetch(`${url}?mode=submission`);
          if (!response.ok) throw new Error((await response.json()).error || "调价清单不可用。");
          onUseFile(new File([await response.blob()], "官方询价-待提交调价.xlsx", { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
          setNotice("已带入下方商品清单。运行预检、核对金额后提交，即会实际修改价格。");
        }
      }
    } catch (cause) { setError(cause instanceof Error ? cause.message : "操作失败。"); }
    finally { setBusy(""); }
  }
  return <details className="workspace-band shipping-pricing-panel" open>
    <summary><Calculator size={18} aria-hidden="true" /><strong>官方费用与调价</strong><span>直发净收益 · 墨西哥 Full 售价</span></summary>
    <div className="shipping-pricing-content">
      <p>直发：填写目标净收益，或自有成本与目标利润。Full：按候选售价查询官方运费和销售费用。</p>
      <div className="shipping-import-actions">
        <div className="field-group"><label htmlFor="official-pricing-file">询价资料（CSV / XLSX）</label><input id="official-pricing-file" type="file" accept=".csv,.xlsx" disabled={pending || Boolean(busy)} onChange={(event) => { setFile(event.target.files?.[0] ?? null); setJob(null); setJobId(null); localStorage.removeItem(storageKey); setError(""); setNotice(""); }} /></div>
        <button className="button secondary" disabled={Boolean(busy)} onClick={() => void action("template")}><Download size={17} />下载询价模板</button>
        <button className="button primary" disabled={!file || pending || Boolean(busy)} onClick={() => void start()}>{pending || busy === "query" ? <LoaderCircle size={17} className="spin" /> : <Calculator size={17} />}{pending ? `官方询价 ${job?.progress ?? 0}%` : "查询官方费用"}</button>
      </div>
      <p className="shipping-rule-note">每次最多 500 行，并发 3。询价不修改商品；结果可下载到本地。费用使用官方返回值，抛重参考除数统一为 5000。</p>
      {pending ? <p role="status">{job?.status === "QUEUED" ? "已进入后台队列" : "正在查询官方运费和销售费用"}，离开页面后仍会继续，返回可查看进度。</p> : null}
      {error ? <div className="form-error" role="alert">{error}</div> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {result ? <>
        <MetricStrip items={[{ label: "询价行数", value: result.summary.total }, { label: "取得报价", value: result.summary.quoted }, { label: "可带入调价", value: result.summary.eligible, tone: "positive" }, { label: "查询失败", value: result.summary.failed }]} />
        <div className="table-scroll"><table className="data-table"><thead><tr><th>商品 / 场景</th><th>目标金额</th><th>官方运费</th><th>销售费用</th><th>计费重量 g</th><th>预计利润</th><th>状态与说明</th></tr></thead><tbody>
          {result.rows.slice((page - 1) * 20, page * 20).map(({ rowNo, quote: q, input, error: rowError }) => <tr key={rowNo}>
            <td><strong>{q?.merchantSku || q?.marketplaceItemId || String(input.merchant_sku || input.marketplace_item_id || `第 ${rowNo} 行`)}</strong><small>{q ? `${q.siteId} · ${q.logisticType === "remote" ? "直发" : "Full"}${q.synthetic ? " · 场景模拟" : ""}` : "查询失败"}</small></td>
            <td>{number(q?.targetNetProceeds ?? q?.candidatePrice)} {q?.currencyId}<small>{q?.pricingMode === "net_proceeds" ? "目标净收益" : "候选售价"}</small></td>
            <td>{number(q?.shippingCost)} {q?.currencyId}<small>{q?.pricingMode === "net_proceeds" ? "当前费用" : "已含官方折扣"}</small></td>
            <td>{number(q?.saleFee)} {q?.currencyId}<small>{q?.pricingMode === "net_proceeds" ? "商品保存分项" : "已含固定费"}</small></td>
            <td>{number(q?.officialWeightG)}<small>{q?.referenceWeightG != null ? `5000 参考：${number(q.referenceWeightG)}` : "官方返回"}</small></td>
            <td>{number(q?.estimatedProfit)} {q?.currencyId}</td>
            <td className="shipping-result-note"><strong>{q?.submission ? "可带入调价" : rowError ? "查询失败" : "仅供核对"}</strong><details><summary>查看说明</summary>{rowError || q?.notes.map((note, index) => <p key={index}>{note}</p>)}</details></td>
          </tr>)}
        </tbody></table></div>
        <Pagination page={page} pageSize={20} total={result.summary.total} onChange={setPage} />
        <div className="shipping-import-actions">
          <button className="button secondary" disabled={Boolean(busy)} onClick={() => void action("export")}><Download size={17} />下载全部费用明细</button>
          <button className="button primary" disabled={disabled || Boolean(busy) || !result.summary.eligible} onClick={() => void action("use")}><ArrowDownToLine size={17} />带入调价清单（{result.summary.eligible} 行）</button>
        </div>
      </> : null}
    </div>
  </details>;
}
